import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { FaArrowRight, FaCheck, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import LandingNavbar from './Navbar';
import LandingLogo from './LandingLogo';
import ScrollReveal from './ScrollReveal';
import { ETHOSBOARD_CONTACT } from './landingConstants';
import {
  LEARNING_BANNERS,
  WORKFORCE_TRANSFORMATION,
  AI_FUTURE,
  IMPACT_STATS,
  SKILLS_BASED,
} from './landingContent';
import EduGenieShowcase from '../../components/Landing/EduGenieShowcase';
import './landing-animations.css';
import './landing-theme.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [bannerIndex, setBannerIndex] = useState(0);
  const [aiTab, setAiTab] = useState(AI_FUTURE.items[0].id);

  const activeBanner = LEARNING_BANNERS[bannerIndex];
  const activeAi = AI_FUTURE.items.find((item) => item.id === aiTab) || AI_FUTURE.items[0];

  const handleNextBanner = useCallback(() => {
    setBannerIndex((prev) => (prev + 1) % LEARNING_BANNERS.length);
  }, []);

  const handlePrevBanner = useCallback(() => {
    setBannerIndex((prev) => (prev - 1 + LEARNING_BANNERS.length) % LEARNING_BANNERS.length);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(handleNextBanner, 7000);
    return () => window.clearInterval(timer);
  }, [handleNextBanner]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const roleStr = String(user.role).toLowerCase();
    const roleNum = parseInt(user.role, 10);
    if (roleStr === 'admin' || roleNum === 0) {
      navigate('/admin/dashboard', { replace: true });
    } else if (roleStr === 'user' || roleNum === 1) {
      navigate('/user/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleBannerCta = (path) => {
    if (path.startsWith('/#')) {
      const hash = path.slice(1);
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      navigate('/');
      window.setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return;
    }
    navigate(path);
  };

  const handleBannerKeyDown = (event) => {
    if (event.key === 'ArrowRight') handleNextBanner();
    if (event.key === 'ArrowLeft') handlePrevBanner();
  };

  return (
    <div className="landing-page min-vh-100 d-flex flex-column">
      <ScrollReveal />
      <LandingNavbar />

      {/* Learning banners */}
      <section
        className="ipl-banner"
        aria-roledescription="carousel"
        aria-label="Learning banners"
        onKeyDown={handleBannerKeyDown}
      >
        {LEARNING_BANNERS.map((banner, index) => (
          <div
            key={banner.id}
            className={`ipl-banner-slide${index === bannerIndex ? ' is-active' : ''}`}
            aria-hidden={index !== bannerIndex}
          >
            <img src={banner.image} alt="" className="ipl-banner-media" />
            <div className="ipl-banner-overlay" />
            <Container className="ipl-banner-content">
              <p className="ipl-banner-eyebrow">{banner.eyebrow}</p>
              <h1 className="ipl-banner-title">{banner.title}</h1>
              <p className="ipl-banner-subtitle">{banner.subtitle}</p>
              <div className="d-flex flex-wrap gap-3">
                <button
                  type="button"
                  className="landing-btn-primary"
                  onClick={() => handleBannerCta(banner.ctaPath)}
                >
                  {banner.ctaText}
                  <FaArrowRight size={14} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="landing-btn-secondary"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </button>
              </div>
            </Container>
          </div>
        ))}

        <button
          type="button"
          className="ipl-banner-nav ipl-banner-nav-prev"
          onClick={handlePrevBanner}
          aria-label="Previous banner"
        >
          <FaChevronLeft aria-hidden="true" />
        </button>
        <button
          type="button"
          className="ipl-banner-nav ipl-banner-nav-next"
          onClick={handleNextBanner}
          aria-label="Next banner"
        >
          <FaChevronRight aria-hidden="true" />
        </button>

        <div className="ipl-banner-dots" role="tablist" aria-label="Banner slides">
          {LEARNING_BANNERS.map((banner, index) => (
            <button
              key={banner.id}
              type="button"
              role="tab"
              aria-selected={index === bannerIndex}
              aria-label={`Show banner ${index + 1}`}
              className={`ipl-banner-dot${index === bannerIndex ? ' is-active' : ''}`}
              onClick={() => setBannerIndex(index)}
            />
          ))}
        </div>
      </section>

      {/* Workforce Transformation */}
      <section id="workforce" className="ipl-section py-5">
        <Container>
          <div className="text-center mb-5 reveal reveal-up">
            <h2 className="landing-section-title mb-3">{WORKFORCE_TRANSFORMATION.title}</h2>
            <p className="landing-section-desc mx-auto mb-0">{WORKFORCE_TRANSFORMATION.description}</p>
          </div>
          <Row className="g-4">
            {WORKFORCE_TRANSFORMATION.pillars.map((pillar) => (
              <Col md={4} key={pillar.title}>
                <article className="ipl-pillar-card reveal reveal-up">
                  <div className="ipl-pillar-media">
                    <img src={pillar.image} alt="" />
                  </div>
                  <div className="ipl-pillar-body">
                    <h3 className="ipl-pillar-title">{pillar.title}</h3>
                    <button
                      type="button"
                      className="ipl-pillar-link"
                      onClick={() => navigate(pillar.path)}
                      aria-label={`${pillar.cta} — ${pillar.title}`}
                    >
                      {pillar.cta}
                      <FaArrowRight size={12} aria-hidden="true" />
                    </button>
                  </div>
                </article>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* AI-Powered Future */}
      <section id="ai-future" className="ipl-section ipl-section-tint py-5">
        <Container>
          <div className="text-center mb-5 reveal reveal-up">
            <h2 className="landing-section-title mb-3">{AI_FUTURE.title}</h2>
            <p className="landing-section-desc mx-auto mb-0">{AI_FUTURE.subtitle}</p>
          </div>

          <div className="ipl-ai-tabs reveal reveal-up" role="tablist" aria-label="AI capabilities">
            {AI_FUTURE.items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={aiTab === item.id}
                className={`ipl-ai-tab${aiTab === item.id ? ' is-active' : ''}`}
                onClick={() => setAiTab(item.id)}
              >
                <img src={item.icon} alt="" className="ipl-ai-tab-icon" />
                <span>{item.title}</span>
              </button>
            ))}
          </div>

          <Row className="align-items-center g-4 g-lg-5 mt-1 reveal reveal-up">
            <Col lg={6}>
              <p className="ipl-ai-short">{activeAi.short}</p>
              <h3 className="ipl-ai-heading">{activeAi.title}</h3>
              <p className="ipl-ai-body">{activeAi.body}</p>
              <button
                type="button"
                className="landing-btn-primary mt-2"
                onClick={() => navigate('/enquiry')}
              >
                Learn More
                <FaArrowRight size={14} aria-hidden="true" />
              </button>
            </Col>
            <Col lg={6}>
              <div className="ipl-ai-visual">
                <img src={activeAi.image} alt={activeAi.title} />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Impact stats */}
      <section className="ipl-stats" aria-label="Impact statistics">
        <Container>
          <Row className="g-4 text-center">
            {IMPACT_STATS.map(({ value, label }) => (
              <Col key={label} xs={6} md>
                <div className="ipl-stat">
                  <div className="ipl-stat-value">{value}</div>
                  <p className="ipl-stat-label mb-0">{label}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Ethora Bot */}
      <EduGenieShowcase />

      {/* Skills-Based Future */}
      <section id="skills-based" className="ipl-section py-5">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6} className="reveal reveal-up">
              <p className="landing-section-eyebrow mb-2">{SKILLS_BASED.eyebrow}</p>
              <h2 className="landing-section-title mb-3">{SKILLS_BASED.title}</h2>
              <p className="landing-section-desc mb-4">{SKILLS_BASED.description}</p>
              <ul className="ipl-skills-list">
                {SKILLS_BASED.points.map((point) => (
                  <li key={point}>
                    <FaCheck aria-hidden="true" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="ipl-skills-tags">
                {SKILLS_BASED.tags.map((tag) => (
                  <span key={tag} className="ipl-skills-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </Col>
            <Col lg={6} className="reveal reveal-up">
              <div className="ipl-skills-visual">
                <img src={SKILLS_BASED.image} alt="Skills-based workforce model" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="landing-cta-band py-5 text-white">
        <Container>
          <Row className="justify-content-center text-center py-3">
            <Col lg={8}>
              <h2 className="landing-section-title text-white mb-3">
                Let&apos;s Start a Conversation
              </h2>
              <p className="lead mb-4 opacity-75">
                Unlock new possibilities — develop talent, empower teams, and transform your organization.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                <button type="button" className="landing-btn-primary" onClick={() => navigate('/enquiry')}>
                  Connect With Us
                  <FaArrowRight size={14} aria-hidden="true" />
                </button>
                <button type="button" className="landing-btn-secondary" onClick={() => navigate('/login')}>
                  Sign In
                </button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <footer className="landing-footer py-5 mt-auto">
        <Container>
          <Row className="mb-4 g-4">
            <Col lg={4}>
              <LandingLogo variant="dark" layout="lockup" size="md" to={null} showProductName={false} />
              <p className="mt-3 mb-3 small">
                Workforce transformation for the Human+AI era — learning that develops talent, empowers teams,
                and transforms organizations.
              </p>
            </Col>
            <Col lg={2} md={6}>
              <h6 className="landing-footer-brand fw-bold mb-3">Quick Links</h6>
              <ul className="list-unstyled small">
                {[
                  { href: '/', label: 'Home' },
                  { href: '/#workforce', label: 'Workforce' },
                  { href: '/#ai-future', label: 'AI Learning' },
                  { href: '/#skills-based', label: 'Skills-Based' },
                  { href: '/enquiry', label: 'Enquiry' },
                ].map(({ href, label }) => (
                  <li key={href} className="mb-2">
                    <a href={href} className="landing-footer-link">
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </Col>
            <Col lg={3} md={6}>
              <h6 className="landing-footer-brand fw-bold mb-3">Focus Areas</h6>
              <ul className="list-unstyled small">
                {['Develop Talent', 'Empower Teams', 'Transform Organizations', 'AI-Powered Learning', 'Skills-Based Design'].map(
                  (item) => (
                    <li key={item} className="mb-2">
                      <span className="landing-footer-link">{item}</span>
                    </li>
                  )
                )}
              </ul>
            </Col>
            <Col lg={3}>
              <h6 className="landing-footer-brand fw-bold mb-3">Contact</h6>
              <p className="small mb-2">
                <a className="landing-footer-link" href={`mailto:${ETHOSBOARD_CONTACT.salesEmail}`}>
                  Email: {ETHOSBOARD_CONTACT.salesEmail}
                </a>
              </p>
              <p className="small mb-2">
                <a className="landing-footer-link" href={`tel:${ETHOSBOARD_CONTACT.phone.replace(/\s/g, '')}`}>
                  Phone: {ETHOSBOARD_CONTACT.phone}
                </a>
              </p>
              <p className="small mb-1 fw-semibold">{ETHOSBOARD_CONTACT.hqLabel}</p>
              {ETHOSBOARD_CONTACT.hqAddress.map((line) => (
                <p key={line} className="small mb-0 opacity-75">{line}</p>
              ))}
              <p className="small mb-1 mt-3 fw-semibold">{ETHOSBOARD_CONTACT.indiaLabel}</p>
              {ETHOSBOARD_CONTACT.indiaAddress.map((line) => (
                <p key={line} className="small mb-0 opacity-75">{line}</p>
              ))}
            </Col>
          </Row>
          <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
          <p className="text-center small mb-0 mt-4">
            © {new Date().getFullYear()} Ethosboard. All rights reserved.
          </p>
        </Container>
      </footer>

      {/* Screen-reader live region for active banner */}
      <div className="visually-hidden" aria-live="polite">
        Showing banner: {activeBanner.title}
      </div>
    </div>
  );
};

export default LandingPage;
